// Predefined colour palette for the `gmail_inboxes.color` column.
//
// Each key maps to one Tailwind class set, used for the inline inbox label
// pills on ticket rows and for the identity dot in the InboxSwitcher, plus a
// `swatch` for the colour picker on /inboxes.
//
// A `chip` variant (filled background + ring, for the switcher trigger button)
// used to live here as well. The trigger was the loudest element in the top
// nav — louder than the active nav item, which inverts the hierarchy — so it
// now uses a neutral chip with a small colour dot instead. That left
// `inboxChipClasses` with no callers, so it was removed rather than kept
// "just in case".
//
// Confidential inboxes always use the critical role regardless of the colour
// column.
//
// These deliberately stay raw Tailwind hues rather than moving to the semantic
// --tint-* roles the rest of the app uses. An inbox colour is CATEGORICAL — the
// user picks "Teal" because it's distinguishable from "Amber", not because the
// inbox is a warning. Forcing eight user-chosen labels through eight
// meaning-carrying roles would make "Rose" imply "critical".
//
// They do need explicit `dark:` variants, since there's no token underneath to
// swap for them. That variant is bound to `[data-theme="dark"]` (see the
// @custom-variant in index.css), so unlike before it only fires when the rest
// of the palette has actually flipped with it.

export const INBOX_COLOR_OPTIONS = [
  { key: 'blue',    label: 'Blue',    badge: 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:ring-blue-900',                   swatch: 'bg-blue-500' },
  { key: 'indigo',  label: 'Indigo',  badge: 'bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-950 dark:text-indigo-300 dark:ring-indigo-900',       swatch: 'bg-indigo-500' },
  { key: 'violet',  label: 'Violet',  badge: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:ring-violet-900',       swatch: 'bg-violet-500' },
  { key: 'emerald', label: 'Emerald', badge: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:ring-emerald-900', swatch: 'bg-emerald-500' },
  { key: 'amber',   label: 'Amber',   badge: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:ring-amber-900',             swatch: 'bg-amber-500' },
  { key: 'orange',  label: 'Orange',  badge: 'bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:ring-orange-900',       swatch: 'bg-orange-500' },
  { key: 'teal',    label: 'Teal',    badge: 'bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-950 dark:text-teal-300 dark:ring-teal-900',                   swatch: 'bg-teal-500' },
  { key: 'rose',    label: 'Rose',    badge: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950 dark:text-rose-300 dark:ring-rose-900',                   swatch: 'bg-rose-500' },
] as const;

export type InboxColorKey = typeof INBOX_COLOR_OPTIONS[number]['key'];

const COLOR_MAP = Object.fromEntries(INBOX_COLOR_OPTIONS.map((c) => [c.key, c])) as Record<
  InboxColorKey,
  typeof INBOX_COLOR_OPTIONS[number]
>;

/** Fallback for an inbox with no colour set. */
const NEUTRAL = {
  badge: 'bg-tint-neutral text-tint-neutral-fg ring-tint-neutral-ring',
  swatch: 'bg-tint-neutral-fg',
};

// Confidential is the one inbox colour that IS semantic — it overrides the
// user's choice to say "handle with care" — so it goes through the critical
// role and themes with everything else.
const CONFIDENTIAL = {
  badge: 'bg-tint-critical text-tint-critical-fg ring-tint-critical-ring',
};

export function inboxBadgeClasses(
  inbox: { color?: string | null; is_confidential?: boolean } | null | undefined,
): string {
  if (!inbox) return NEUTRAL.badge;
  if (inbox.is_confidential) return CONFIDENTIAL.badge;
  const c = inbox.color ? COLOR_MAP[inbox.color as InboxColorKey] : undefined;
  return c ? c.badge : NEUTRAL.badge;
}
